import { computed, inject, Injectable, Injector, Signal, signal, WritableSignal } from '@angular/core';
import { SmartDeviceApiService } from '@dc-api/smart-device.api.service';
import { SmartDeviceNetwork } from '@dc-core/dc-backend/dc-classes';
import moment from 'moment';
import { VirtLocalStorageKeys } from 'src/app/constants/local-storage.keys';

import {
    DartCounterSmartDevice,
    OmniErrorState,
    VirtFirmwareSettings,
    VirtSettings,
} from '../../models/smart-devices.models';
import { SmartDevicesStateService } from '../../services/smart-devices-state.service';

export enum SmartDeviceStatus {
    OWNED = 'OWNED',
    ACTIVATED = 'ACTIVATED',
}

@Injectable({
    providedIn: 'root',
})
export class VirtStateService {
    private injector: Injector = inject(Injector);

    private smartDevice: WritableSignal<DartCounterSmartDevice> = signal(null);
    private smartDeviceNetwork: WritableSignal<SmartDeviceNetwork> = signal(null);
    private firmwareSettings: WritableSignal<VirtFirmwareSettings> = signal({
        camera_position: 'top',
    });

    private deviceSettings: WritableSignal<VirtSettings> = signal({});
    private deviceErrorState: WritableSignal<OmniErrorState> = signal(null);
    private deviceStatus: WritableSignal<SmartDeviceStatus> = signal(null);

    private get smartDeviceApiService(): SmartDeviceApiService {
        return this.injector.get(SmartDeviceApiService);
    }
    private get smartDevicesStateService(): SmartDevicesStateService {
        return this.injector.get(SmartDevicesStateService);
    }

    public currentSmartDevice: Signal<DartCounterSmartDevice> = computed(() => this.smartDevice());
    public currentSmartDeviceNetwork: Signal<SmartDeviceNetwork> = computed(() => this.smartDeviceNetwork());
    public currentFirmwareSettings: Signal<VirtFirmwareSettings> = computed(() => this.firmwareSettings());
    public currentDeviceSettings: Signal<VirtSettings> = computed(() => this.deviceSettings());

    public currentDeviceStatus: Signal<SmartDeviceStatus> = computed(() => this.deviceStatus());

    constructor() {
        this.restoreStoredFirmwareSettings();
        this.restoreStoredDeviceSettings();
    }

    public setCurrentSmartDevice(smartDevice: DartCounterSmartDevice): void {
        this.smartDevice.set(smartDevice);
    }

    public setCurrentSmartDeviceNetwork(smartDeviceNetwork: SmartDeviceNetwork): void {
        this.smartDeviceNetwork.set(smartDeviceNetwork);
    }

    public setDeviceStatus(status: SmartDeviceStatus): void {
        this.deviceStatus.set(status);
    }

    public setDeviceErrorState(state: OmniErrorState): void {
        this.deviceErrorState.set(state);
    }

    public updateDeviceSettings(updater: (settings: VirtSettings) => Partial<VirtSettings>): void {
        this.deviceSettings.update((settings) => ({ ...settings, ...updater(settings) }));
        localStorage.setItem(VirtLocalStorageKeys.virtSettings, JSON.stringify(this.deviceSettings()));
    }

    public updateFirmwareSettings(updater: (settings: VirtFirmwareSettings) => Partial<VirtFirmwareSettings>): void {
        this.firmwareSettings.update((settings) => ({ ...settings, ...updater(settings) }));
        localStorage.setItem(VirtLocalStorageKeys.virtFirmwareSettings, JSON.stringify(this.firmwareSettings()));
    }

    public clearCurrentSmartDevice(): void {
        this.setDeviceStatus(SmartDeviceStatus.OWNED);
        this.smartDevice.set(null);
    }

    public clearDeviceErrorState(): void {
        this.deviceErrorState.set(null);
    }

    public updateCurrentSmartDevice(
        updater: (smartDevice: DartCounterSmartDevice) => Partial<DartCounterSmartDevice>
    ): Promise<any> {
        this.smartDevice.update((device) => ({
            ...device,
            ...updater(device),
        }));

        return Promise.resolve();
    }

    public updateLastCalibratedAt(): void {
        this.smartDeviceApiService
            .updateSmartDeviceById({
                smartDeviceId: this.smartDevice().id,
                last_calibrated_at: moment().utc().toISOString(),
            })
            .then((res) => {
                this.smartDevice.update((smartDevice) => ({
                    ...smartDevice,
                    last_calibrated_at: res.data.last_calibrated_at,
                }));

                this.smartDevicesStateService.updateSmartDeviceInNetwork(this.smartDevice().id, () => ({
                    last_calibrated_at: res.data.last_calibrated_at,
                }));
            })
            .catch(console.error);
    }

    private restoreStoredFirmwareSettings(): void {
        const debugSettings = localStorage.getItem(VirtLocalStorageKeys.virtFirmwareSettings);

        if (debugSettings) {
            try {
                const parsed: VirtFirmwareSettings = JSON.parse(debugSettings);

                this.firmwareSettings.update((current) => ({
                    ...current,
                    camera_postion: parsed.camera_position ?? current.camera_position,
                }));
            } catch (error) {
                console.warn('Invalid omni debug settings in localStorage:', error);
            }
        }
    }

    private restoreStoredDeviceSettings(): void {
        const omniSettings = localStorage.getItem(VirtLocalStorageKeys.virtSettings);

        if (omniSettings) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
                const parsed: VirtSettings = JSON.parse(omniSettings);

                this.deviceSettings.update((current) => ({
                    ...current,
                }));
            } catch (error) {
                console.warn('Invalid omni debug settings in localStorage:', error);
            }
        }
    }
}
